import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

/**
 * Count a sponsored block as seen, once, when it is actually on screen.
 *
 * Views and clicks together are what a sponsorship is sold on, so the view
 * has to mean something defensible. Counting at render would bill a partner
 * for a block four screens below the fold that nobody scrolled to, which is
 * the kind of number that ends a relationship the first time they check it
 * against their own analytics.
 *
 * Counted once per mount: scrolling past the same block four times is one
 * view of one advert.
 *
 * Returns a ref to attach to the block's outermost element. Everything about
 * it is best-effort — no observer, a blocked request, an ad blocker — because
 * the advert is the product and the counting is bookkeeping. It must never
 * stop the block rendering.
 */
export default function useSponsorImpression(sponsorId) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!sponsorId || seen || !ref.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const el = ref.current;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) return;
      setSeen(true);
      observer.disconnect();
      api.post(`/sponsors/${sponsorId}/impression`).catch(() => {});
    }, { threshold: [0.5] });

    observer.observe(el);
    return () => observer.disconnect();
  }, [sponsorId, seen]);

  return ref;
}
